import Alert from "@mui/material/Alert";
import Logger from "../../services/Logger";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import DictionaryStatsService from "../../services/DictionaryStatsService";

export default function DictionaryStats() {
    const [dictionaryData, setDictionaryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadDictionaryData();
    }, []);

    const loadDictionaryData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await DictionaryStatsService.getDictionaryStats();
            Logger.log("Dictionary stats data:", data);
            setDictionaryData(data);
        } catch (err) {
            Logger.error("Error loading dictionary data:", err);
            setError("Failed to load dictionary statistics. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px'
            }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ ml: 2 }}>
                    Loading Dictionary statistics...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ flexGrow: 1 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!dictionaryData) {
        return (
            <Box sx={{ flexGrow: 1 }}>
                <Alert severity="warning">
                    No Dictionary data available.
                </Alert>
            </Box>
        );
    }

    // Calculate totals across all languages
    const totalWords = Object.values(dictionaryData).reduce((sum, lang) => sum + (lang.total || 0), 0);
    const totalToday = Object.values(dictionaryData).reduce((sum, lang) => sum + (lang.today || 0), 0);

    return (
        <Grid container spacing={2} sx={{ p: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            {/* Header Section */}
            <Grid size={{ xs: 12 }} sx={{ width: '100%' }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Dictionary Statistics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Overview of dictionary word counts and daily additions
                </Typography>
            </Grid>

            {/* Summary Cards */}
            <Grid size={{ xs: 12 }} container spacing={2}>
                <Card sx={{ height: '100%', flex: 1 }}>
                    <CardContent>
                        <Typography variant="h6" color="primary" gutterBottom>
                            Total Words
                        </Typography>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                            {totalWords.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Words in dictionary
                        </Typography>
                    </CardContent>
                </Card>

                <Card sx={{ height: '100%', flex: 1 }}>
                    <CardContent>
                        <Typography variant="h6" color="primary" gutterBottom>
                            Today's Words
                        </Typography>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                            {totalToday.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            New words added today
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>

            {/* Language-specific Cards */}
            <Grid size={{ xs: 12 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mt: 2 }}>
                    By Language
                </Typography>
            </Grid>
            
            {Object.entries(dictionaryData).map(([language, data]) => (
                <Grid size={{ xs: 12, md: 6 }} key={language}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" color="primary" gutterBottom sx={{ textTransform: 'capitalize' }}>
                                {language} Dictionary
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                                        {data.total?.toLocaleString() || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Words
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                                        {data.today?.toLocaleString() || 0}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Added Today
                                    </Typography>
                                </Box>
                            </Box>
                            {data.today > 0 && data.total > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Growth Rate:</strong> {((data.today / data.total) * 100).toFixed(2)}% increase today
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            ))}

            {/* Additional Information */}
            <Grid size={{ xs: 12 }}>
                <Box sx={{ 
                    p: 3, 
                    backgroundColor: 'grey.50', 
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                }}>
                    <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Dictionary Overview
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        The dictionary contains a comprehensive collection of words used across all puzzle types. 
                        This includes words for hidden word puzzles, crosswords, and other word-based games.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Total Dictionary Size:</strong> {totalWords.toLocaleString()} words
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Daily Growth:</strong> {totalToday.toLocaleString()} new words added today
                        </Typography>
                        {totalToday > 0 && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>Overall Growth Rate:</strong> {((totalToday / totalWords) * 100).toFixed(2)}% increase today
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
}
